import { Trans } from "@lingui/macro";
import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { DestinationMenuEntry } from "~/applications/OfferSearch/Domain/DestinationPage";
import { Guests } from "~/applications/OfferSearch/Domain/Guests";
import { TravelPurpose } from "~/applications/OfferSearch/Domain/TravelPurpose";
import DatesMenuWithTextualTrigger from "~/applications/OfferSearch/Ui/StayRequestForm/DatesMenuWithTextualTrigger";
import DestinationMenuWithTextualTrigger from "~/applications/OfferSearch/Ui/StayRequestForm/DestinationMenuWithTextualTrigger";
import GuestsMenuWithTextualTrigger from "~/applications/OfferSearch/Ui/StayRequestForm/GuestsMenuWithTextualTrigger";
import StayRequest from "~/core/StayRequest";
import { Route } from "~/core/routes";
import { Search } from "~/guidelines/Icon";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import { body1 } from "~/guidelines/Typography";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0, 3)};
  flex-wrap: wrap;

  ${body1};

  ${mediaQueries.md()} {
    flex-direction: row;
  }
`;

const TextRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export type TextualStayRequestFormProps = {
  destination: DestinationMenuEntry;
  destinations: Array<DestinationMenuEntry>;
  stayRequest: StayRequest | undefined;
  onDestinationChange: (newDestination: DestinationMenuEntry) => void;
  onStayRequestChange: (newStayRequest: StayRequest) => void;
  offerMaxCapacity: number | undefined;
  onNewSearch?: (
    newGuests: {
      nbAdults: number;
      nbChildren: number;
      nbBabies: number;
    },
    newTravelPurpose: TravelPurpose,
  ) => Route;
};

const TextualStayRequestForm = ({
  destination,
  stayRequest,
  destinations,
  onDestinationChange,
  onStayRequestChange,
  offerMaxCapacity,
  onNewSearch,
}: TextualStayRequestFormProps) => {
  const [guests, setGuests] = useState<Guests | undefined>(stayRequest?.guests);

  const [travelPurpose, setTravelPurpose] = useState<TravelPurpose | undefined>(
    stayRequest?.travelPurpose,
  );

  const [dates, setDates] = useState<
    | {
        checkIn: Date;
        checkOut: Date;
      }
    | undefined
  >(() =>
    stayRequest
      ? {
          checkIn: stayRequest.checkIn,
          checkOut: stayRequest.checkOut,
        }
      : undefined,
  );

  const handleSubmitGuestsMenu = useCallback(
    (
      newGuests: {
        nbAdults: number;
        nbChildren: number;
        nbBabies: number;
      },
      newTravelPurpose: TravelPurpose,
    ) => {
      if (dates !== undefined) {
        onStayRequestChange(
          new StayRequest(
            dates.checkIn,
            dates.checkOut,
            newGuests.nbAdults,
            newGuests.nbChildren,
            newGuests.nbBabies,
            newTravelPurpose,
          ),
        );
      }

      setGuests({
        nbAdult: newGuests.nbAdults,
        nbChildren: newGuests.nbChildren,
        nbBabies: newGuests.nbBabies,
      });
      setTravelPurpose(newTravelPurpose);
    },
    [dates, onStayRequestChange],
  );

  const handleSubmitDatesMenu = useCallback(
    (newDates: { checkIn: Date; checkOut: Date }) => {
      if (guests !== undefined && travelPurpose !== undefined) {
        onStayRequestChange(
          new StayRequest(
            newDates.checkIn,
            newDates.checkOut,
            guests.nbAdult,
            guests.nbChildren,
            guests.nbBabies,
            travelPurpose,
          ),
        );
      }

      setDates(newDates);
    },
    [guests, travelPurpose, onStayRequestChange],
  );

  return (
    <Container
      key={
        stayRequest ? JSON.stringify(stayRequest.toJSON()) : "no-stay-request"
      }
    >
      <TextRow>
        <Search />
        <Trans>
          At{" "}
          <DestinationMenuWithTextualTrigger
            destination={destination}
            destinations={destinations}
            onDestinationChange={onDestinationChange}
          />
        </Trans>
      </TextRow>

      <TextRow>
        <Trans>
          for{" "}
          <GuestsMenuWithTextualTrigger
            defaultGuests={
              guests
                ? {
                    nbAdults: guests.nbAdult,
                    nbChildren: guests.nbChildren,
                    nbBabies: guests.nbBabies,
                  }
                : undefined
            }
            defaultTravelPurpose={travelPurpose}
            offerMaxCapacity={offerMaxCapacity}
            onSubmit={handleSubmitGuestsMenu}
            onNewSearch={onNewSearch}
          />
        </Trans>
      </TextRow>

      <TextRow>
        <Trans>
          from{" "}
          <DatesMenuWithTextualTrigger
            firstAvailableDate={destination.firstBookableDate}
            dates={
              dates
                ? {
                    checkIn: dates.checkIn,
                    checkOut: dates.checkOut,
                  }
                : undefined
            }
            onSubmit={handleSubmitDatesMenu}
          />
        </Trans>
      </TextRow>
    </Container>
  );
};

export default TextualStayRequestForm;
